import { useEffect } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Tour from 'reactour';

import { AskKim } from './components/ask-kim';
import { CreateSubsetButton } from './components/create-subset-button';
import { MatterOverviewLoader } from './components/matter-overview-loader';
import { ProceduralTimelineWidget } from './components/procedural-timeline-widget';
import { ProcessingPipeline } from './components/processing-pipeline';
import { RecentRunStatus } from './components/recent-run-status';
import { RecentSubsets } from './components/recent-subsets';
import { SubsetStatCard } from './components/subset-stat-card';
import { SuggestedSubset } from './components/suggested-subset';
import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { OrgPermission } from '../../../../../../backend/src/organisation/organisation-permission';
import { MatterType, RunStatus } from '../../../../../../backend/src/types/types';
import { SampleMatterLoader } from '../../../components/sample-matter-loader';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/shadcn/tooltip';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useImportSampleMatter } from '@/api/mutations/useImportSampleMatter';
import { useGetMatterOverview } from '@/api/queries/useGetMatterOverview';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetRecentSubsets } from '@/api/queries/useGetRecentSubsets';
import { useGetSubsets, hasRecentProcessingSubset } from '@/api/queries/useGetSubsets';
import { useGetSummary } from '@/api/queries/useGetSummary';
import { useUserContext } from '@/Contexts/User';
import formatDate from '@/helpers/formatDate';
import { hasAnyOngoingRunOnAllDocsCase } from '@/helpers/runs';
import { useOrgPermissions } from '@/hooks/orgPermissions';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { MatterOverviewOnboarding } from '@/new/components/onboarding/matter-overview-V2-onboarding';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { MATTER_OVERVIEW_TOUR_STEPS } from '@/new/constants/tour-steps';
import { useTour } from '@/new/hooks/useTour';
import { IssuesOverview } from '@/new/pages/matters/:matterId/subsets/:subsetId/components/issues-overview';
import { Summary } from '@/new/pages/matters/:matterId/subsets/:subsetId/components/summary';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';

export function MatterOverview() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const { isOpen: isTourOpen, openTour, closeTour } = useTour('matter-overview');

  const navigate = useNavigate();

  const { user } = useUserContext();
  const { isEnabled } = useOrgPermissions();
  const { data: matter, isLoading: isLoadingMatter } = useGetMatterV2(matterId);
  const allDocumentsSubsetId = matter?.allDocumentsCaseId ?? undefined;
  const { data: overview, isLoading: isLoadingOverview, refetch: refetchOverview } = useGetMatterOverview(matterId);
  const { mutateAsync: importSampleMatter, isLoading: isImportingSampleMatter } = useImportSampleMatter();

  const {
    data: recentSubsets,
    isLoading: isLoadingRecentSubsets,
    refetch: refetchRecentSubsets,
  } = useGetRecentSubsets(matterId);
  const { data: subsets, refetch: refetchSubsets } = useGetSubsets(matterId);
  const hasProcessingSubset = hasRecentProcessingSubset(subsets);

  const {
    data: summaries,
    isLoading: summaryIsLoading,
    refetch: refetchSummaries,
  } = useGetSummary({
    matterId,
    subsetId: allDocumentsSubsetId,
  });

  const handleCreateSampleMatter = async () => {
    trackCustomEvent(TrackingEventsV2.MatterCreateSample, { source: 'matter_homepage_processing' });
    await importSampleMatter(undefined, {
      onSuccess: (data) => {
        navigate(`/matters/${data.matterId}`);
      },
    });
  };

  const isLoading = isLoadingMatter || isLoadingOverview || isLoadingRecentSubsets;

  // Get processing state from store (computed in breadcrumb)
  const processingState = useMatterProcessingStore((state) => state.getProcessingState(matterId));
  const hasCompletedRuns = processingState?.hasCompletedRuns ?? false;
  // Check if there is a run going on the all docs subset
  const areRunsProcessing = hasAnyOngoingRunOnAllDocsCase(
    processingState?.allProcessingRuns,
    allDocumentsSubsetId ?? '',
  );

  // Use the first processing run (already sorted with awaiting approval first)
  const mostRecentProcessingRun = processingState?.allProcessingRuns?.[0] ?? null;
  const hasNoRuns =
    (!processingState?.allProcessingRuns || processingState?.allProcessingRuns.length === 0) &&
    !processingState?.mostRecentRun;

  useEffect(() => {
    if (!areRunsProcessing && matterId && allDocumentsSubsetId) {
      refetchOverview();
      refetchSummaries();
      refetchRecentSubsets();
      refetchSubsets();
    }
  }, [
    areRunsProcessing,
    refetchOverview,
    refetchSummaries,
    refetchRecentSubsets,
    refetchSubsets,
    matterId,
    allDocumentsSubsetId,
  ]);

  if (isLoading) {
    return <MatterOverviewLoader />;
  }

  return (
    <>
      <Breadcrumb
        items={
          matter
            ? [
                { href: '/matters', label: 'Matters', active: false },
                {
                  href: `/matters/${matter.id}`,
                  label: matter.name,
                  active: true,
                  iconClass: 'lucide--folder',
                },
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />

      <PageContainer>
        <PageHeader>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-4 px-0.5 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Icon className="lucide--calendar" />
                <div>Created {formatDate(matter?.createdDate ?? '')}</div>
              </div>

              <Link
                to={`/matters/${matterId}/settings/users`}
                className="flex cursor-pointer items-center gap-2 transition-colors hover:text-gray-700"
              >
                <Icon className="lucide--users-round" />
                <div>
                  <span className="font-semibold">{matter?.users?.length} </span>
                  {matter?.users?.length === 1 ? 'User' : 'Users'} +
                </div>
              </Link>
            </div>

            <PageTitle text={matter?.name ?? ''} />
          </div>

          <div className="flex items-center gap-2">
            {processingState?.hasCompletedRuns && (
              <CreateSubsetButton matterId={matterId} hasProcessingSubset={hasProcessingSubset} />
            )}

            {!matter?.isDraft && (
              <PermissionGate permission={Permission.DocumentUpload} uiMode="disabled">
                {matter?.type === MatterType.Sample ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button variant="primary" disabled data-tour="add-documents-btn">
                          <Icon className="lucide--file-up" />
                          Upload Documents
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>You can't upload documents to a sample Matter.</TooltipContent>
                  </Tooltip>
                ) : matter?.issues ? (
                  <Button variant="primary" asChild data-tour="add-documents-btn">
                    <Link to={`/matters/${matterId}/docs/upload`}>
                      <Icon className="lucide--file-up" />
                      Upload Documents
                    </Link>
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 px-2 text-xs text-gray-600">
                    <Icon className="lucide--info" />
                    <p>
                      <Link className="text-blue-500" to={'settings/context'}>
                        Add Matter issues
                      </Link>
                      <span> to upload more documents</span>
                    </p>
                  </div>
                )}
              </PermissionGate>
            )}

            <PermissionGate
              uiMode="disabled"
              permission={[
                Permission.MatterEdit,
                Permission.DocumentDelete,
                Permission.DocumentDownload,
                Permission.RelativityImport,
                Permission.MatterViewUsers,
                Permission.MatterCreate,
                Permission.AuditView,
              ]}
              orAllowed={matter?.createdById === user?.id}
              disabledMessage="You do not have permission to access Matter settings"
            >
              <Button variant="default" asChild>
                <Link to={`/matters/${matterId}/settings`}>
                  <Icon className="lucide--settings" />
                  Settings
                </Link>
              </Button>
            </PermissionGate>
          </div>
        </PageHeader>

        {processingState?.mostRecentRun && (
          <div className="mb-4">
            <RecentRunStatus
              mostRecentRun={processingState?.mostRecentRun ?? null}
              allDocumentsCaseId={allDocumentsSubsetId}
            />
          </div>
        )}

        {hasCompletedRuns ? (
          <>
            <div className="mb-6 flex flex-wrap items-stretch gap-3" data-tour="matter-stats">
              <SubsetStatCard
                value={hasCompletedRuns ? overview?.totalFacts?.toLocaleString() : undefined}
                to={
                  hasCompletedRuns && allDocumentsSubsetId
                    ? `/matters/${matterId}/subsets/${allDocumentsSubsetId}/facts`
                    : undefined
                }
              >
                <Icon className="lucide--search text-gray-500" />
                <p>Total Facts</p>
              </SubsetStatCard>

              <SubsetStatCard
                value={hasCompletedRuns ? overview?.disputedFacts?.toLocaleString() : undefined}
                to={
                  hasCompletedRuns && allDocumentsSubsetId
                    ? `/matters/${matterId}/subsets/${allDocumentsSubsetId}/inconsistencies`
                    : undefined
                }
              >
                <Icon className="lucide--alert-triangle text-gray-500" />
                <p>Inconsistencies</p>
              </SubsetStatCard>
              <SubsetStatCard
                value={hasCompletedRuns ? overview?.documents?.toLocaleString() : undefined}
                to={
                  hasCompletedRuns && allDocumentsSubsetId
                    ? `/matters/${matterId}/subsets/${allDocumentsSubsetId}/docs`
                    : undefined
                }
              >
                <Icon className="lucide--file-text text-gray-500" />
                <p>Documents</p>
                {overview?.failedDocsCount !== 0 && allDocumentsSubsetId && !areRunsProcessing && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to={`/matters/${matterId}/subsets/${allDocumentsSubsetId}/docs?processed=false`}
                        className="flex items-center gap-1.5 text-red-500 transition-colors hover:text-red-600"
                      >
                        <Icon className="lucide--circle-alert" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {overview?.failedDocsCount} {overview?.failedDocsCount === 1 ? 'document' : 'documents'} in this
                      matter failed to process
                    </TooltipContent>
                  </Tooltip>
                )}
              </SubsetStatCard>
              <SubsetStatCard
                value={hasCompletedRuns ? overview?.entities.toLocaleString() : undefined}
                to={
                  hasCompletedRuns && allDocumentsSubsetId
                    ? `/matters/${matterId}/subsets/${allDocumentsSubsetId}/entities`
                    : undefined
                }
              >
                <Icon className="lucide--users-round text-gray-500" />
                <p>Entities</p>
              </SubsetStatCard>

              <SubsetStatCard
                value={hasCompletedRuns ? overview?.chronologies?.toLocaleString() : undefined}
                to={
                  hasCompletedRuns && allDocumentsSubsetId
                    ? `/matters/${matterId}/subsets/${allDocumentsSubsetId}/chronologies`
                    : undefined
                }
              >
                <Icon className="lucide--git-fork text-gray-500" />
                <p>Chronologies</p>
              </SubsetStatCard>
            </div>

            {allDocumentsSubsetId && (
              <div className="bg-white">
                <AskKim defaultSubsetId={allDocumentsSubsetId} subsets={subsets} />
              </div>
            )}

            {allDocumentsSubsetId &&
              (() => {
                const hasSummary = Boolean(summaries?.content?.[0]?.content?.trim());
                const issues = summaries?.content?.slice(1);
                const hasIssues = (issues && issues.length > 0) || summaryIsLoading;
                const hasContent = hasSummary || hasIssues;

                if (!hasContent) return null;

                return (
                  <div className="mt-8">
                    {hasSummary && (
                      <Summary
                        summaryType="Matter"
                        summaryText={summaries?.content?.[0]?.content ?? ''}
                        isProcessing={areRunsProcessing}
                        isLoading={summaryIsLoading}
                        hasIssues={hasIssues}
                      />
                    )}
                    <IssuesOverview
                      issues={issues ?? []}
                      isLoading={summaryIsLoading}
                      matterId={matterId}
                      caseId={allDocumentsSubsetId}
                      hasSummary={hasSummary}
                    />
                  </div>
                );
              })()}

            {isEnabled(OrgPermission.ProceduralTimelines) && (
              <div className="mt-8">
                <ProceduralTimelineWidget matterId={matterId} />
              </div>
            )}

            <div className="mt-8 mb-6 overflow-hidden rounded-lg border border-gray-200" data-tour="recent-subsets">
              <div className="flex items-center gap-3 border-b bg-gray-50 px-4 py-2.5">
                <Icon className="lucide--group h-4 w-4 text-gray-500" />
                <span className="font-medium text-gray-700">Subsets</span>
              </div>
              <div className="flex gap-6 bg-white p-5 pt-4">
                <div className="flex-1">
                  <RecentSubsets recentlyAccessedSubsets={recentSubsets ?? []} subsets={subsets ?? []} />
                </div>
                <div className="flex-1">
                  <SuggestedSubset />
                </div>
              </div>
            </div>
          </>
        ) : areRunsProcessing && mostRecentProcessingRun && allDocumentsSubsetId ? (
          <AnimatePresence mode="wait">
            {isImportingSampleMatter ? (
              <div className="flex h-full items-center justify-center">
                <SampleMatterLoader />
              </div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 40 }}
                transition={{ duration: 0.3, ease: 'easeIn' }}
                className="h-full"
              >
                <div className="relative w-full overflow-hidden" data-testid="processing-state">
                  <div className="my-8 mb-12 text-center">
                    <h2 className="font-serif text-3xl font-medium">Matter Processing...</h2>
                    <p className="mt-2 text-sm text-gray-600">
                      Once processing is complete, you'll be able to explore your data.
                    </p>

                    <div className="mt-8 flex flex-col items-center gap-2">
                      <Button variant={'primary'} asChild>
                        <Link
                          to={'settings/users'}
                          onClick={() => trackCustomEvent(TrackingEventsV2.UserClickAddWhileProcessing, { matterId })}
                        >
                          <Icon className="lucide--user-2" /> Add other users in your organisation
                        </Link>
                      </Button>

                      <Button
                        variant={'ghost'}
                        onClick={handleCreateSampleMatter}
                        disabled={isImportingSampleMatter}
                        className="group ring-offset-0 duration-150 focus-visible:ring-offset-2"
                      >
                        Or browse a sample Matter while you wait
                        <Icon className="lucide--chevron-right duration-150 group-hover:translate-x-0.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4">
                    <ProcessingPipeline
                      casePipelineStatus={mostRecentProcessingRun.status as RunStatus}
                      requiresAdminApproval={mostRecentProcessingRun.requiresAdminApproval}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        ) : hasNoRuns ? (
          <div className="p-12">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Icon className="lucide--file-up" />
                </EmptyMedia>
                <EmptyTitle>No documents submitted</EmptyTitle>
                <EmptyDescription>
                  Upload and submit documents to start processing your matter. Once processing is complete, you'll be
                  able to explore your data.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        ) : null}
      </PageContainer>

      <MatterOverviewOnboarding onTakeTour={openTour} />

      {/* @ts-ignore: Tour unsupported types */}
      <Tour startAt={0} steps={MATTER_OVERVIEW_TOUR_STEPS} isOpen={isTourOpen} onRequestClose={closeTour} />
    </>
  );
}
